/**
 * Searching Overlay Component
 * Full-screen "Searching Locations..." state with animated globe
 */

import { useEffect, useState } from 'react';
import { Globe, Target } from 'lucide-react';

export default function SearchingOverlay({ imagePreview, isVisible }) {
  const [loadingStep, setLoadingStep] = useState(0);

  const steps = [
    "Analyzing terrain...",
    "Identifying landmarks...",
    "Triangulating position...",
    "Refining coordinates...",
    "Fetching property data..."
  ];

  useEffect(() => {
    if (!isVisible) return;
    setLoadingStep(0); // Reset on open

    const interval = setInterval(() => {
      setLoadingStep(prev => (prev + 1) % steps.length);
    }, 1500);

    return () => clearInterval(interval);
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      {/* Dark backdrop with blur */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md animate-fade-in" />

      {/* Center card */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Animated Radar Icon */}
        <div className="relative w-32 h-32 mb-8 flex items-center justify-center">
          {/* Pulsing rings */}
          <div className="absolute inset-0 rounded-full border border-primary-500/30 animate-radar-ping" />
          <div className="absolute inset-0 rounded-full border border-primary-500/20 animate-radar-ping" style={{ animationDelay: '0.6s' }} />

          {/* Center Globe */}
          <div className="relative z-10 bg-black/50 p-4 rounded-full border border-primary-500/50 shadow-[0_0_30px_rgba(16,185,129,0.3)] backdrop-blur-sm animate-pulse-soft">
            <Globe className="w-12 h-12 text-primary-500" strokeWidth={1.5} />
          </div>

          {/* Rotating scanner */}
          <div className="absolute inset-0 rounded-full border-t-2 border-primary-500/80 animate-spin-slow" />
        </div>

        {/* Text Status */}
        <div className="text-center space-y-2">
          <h3 className="text-3xl font-bold text-white tracking-tight animate-fade-in">
            Searching Locations
          </h3>
          <div className="h-6 overflow-hidden">
            <p className="text-primary-400 font-mono text-sm uppercase tracking-widest animate-fade-in key={loadingStep}">
              {steps[loadingStep]}
            </p>
          </div>
        </div>
      </div>

      {/* Image thumbnail (bottom right) */}
      {imagePreview && (
        <div className="absolute bottom-8 right-8 w-48 h-32 rounded-xl overflow-hidden
                      border border-white/20 shadow-2xl pointer-events-auto bg-black/80 animate-slide-up">
          <img
            src={imagePreview}
            alt="Analyzing"
            className="w-full h-full object-cover opacity-60"
          />
          {/* Scanning Line */}
          <div className="absolute left-0 right-0 h-0.5 bg-primary-500 shadow-[0_0_10px_#10b981] animate-scan" />

          <div className="absolute bottom-2 right-2 flex items-center gap-1.5 px-2 py-1 bg-black/60 backdrop-blur rounded text-[10px] text-white/80 font-mono border border-white/10">
            <Target size={10} className="text-primary-500 animate-pulse" />
            SCANNING SOURCE
          </div>
        </div>
      )}
    </div>
  );
}
